import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FILTER_CATEGORY_LABELS,
  FILTER_IDS,
  HORIZONTAL_CHIP_ORDER,
} from "../FilterBar/filterIds";
import { renderFilterPanel } from "../FilterBar/renderFilterPanel";
import {
  MOBILE_PAGE_PAD_X,
  MOBILE_SHEET_EXPANDED_VH,
  MOBILE_TOUCH_MIN_PX,
  MOBILE_TYPE,
} from "../../../theme/mobileLayout";

/**
 * Full-height mobile filter sheet.
 * Defaults to Search company filters; pass chipOrder / categoryLabels / renderPanel
 * to reuse the same shell for Conferences (or other domains).
 * Draft edits remain staged until a commit action; Cancel discards them.
 */
export default function MobileFilterSheet({
  open,
  onClose,
  controller,
  initialFilterId = null,
  showNaturalLanguageFilter = false,
  // Keep this reference stable; the expansion-reset effect depends on its identity.
  chipOrder = HORIZONTAL_CHIP_ORDER,
  categoryLabels = FILTER_CATEGORY_LABELS,
  renderPanel = renderFilterPanel,
  keepPanelsMounted = false,
  testId = "mobile-filter-sheet",
}) {
  const {
    summaries = {},
    showClearFilters,
    clearDraftFilters,
    applyFilters,
    panelContext,
    filtersDisabled,
    commitFilters,
    discardFilters,
    hasUncommittedFilters,
  } = controller || {};

  const defaultExpanded = initialFilterId || chipOrder[0] || false;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const panelApplyRefs = useRef(new Map());

  const registerPanelApply = useCallback((panelId, applyPanelDraft) => {
    panelApplyRefs.current.set(panelId, applyPanelDraft);
    return () => {
      if (panelApplyRefs.current.get(panelId) === applyPanelDraft) {
        panelApplyRefs.current.delete(panelId);
      }
    };
  }, []);

  const applyActivePanel = () => {
    if (commitFilters) {
      commitFilters();
    } else if (applyFilters) {
      applyFilters();
    } else {
      panelApplyRefs.current.forEach((applyPanelDraft) => applyPanelDraft());
    }
    onClose();
  };

  useEffect(() => {
    if (open && initialFilterId) {
      setExpanded(initialFilterId);
    } else if (open && !initialFilterId && chipOrder[0]) {
      setExpanded(chipOrder[0]);
    }
  }, [open, initialFilterId, chipOrder]);

  const filterOrder = showNaturalLanguageFilter
    && !chipOrder.includes(FILTER_IDS.NATURAL_LANGUAGE)
    ? [...chipOrder, FILTER_IDS.NATURAL_LANGUAGE]
    : chipOrder;
  const activeCount = filterOrder.filter((id) => summaries[id]?.isActive).length;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={(_event, reason) => {
        if (reason === "escapeKeyDown") {
          discardFilters?.();
          onClose();
        } else {
          applyActivePanel();
        }
      }}
      onOpen={() => {}}
      disableSwipeToOpen
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        "data-testid": testId,
        sx: {
          height: `${MOBILE_SHEET_EXPANDED_VH}vh`,
          maxHeight: `${MOBILE_SHEET_EXPANDED_VH}vh`,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          display: "flex",
          flexDirection: "column",
          pb: "env(safe-area-inset-bottom, 0px)",
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: 999,
          bgcolor: "rgba(88, 89, 91, 0.28)",
          mx: "auto",
          mt: 1.25,
          mb: 1,
          flexShrink: 0,
        }}
        aria-hidden
      />
      <Box
        sx={{
          px: MOBILE_PAGE_PAD_X,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ ...MOBILE_TYPE.pageTitle, fontSize: 18 }}>
          Filters{activeCount > 0 ? ` (${activeCount})` : ""}
        </Typography>
        {hasUncommittedFilters ? (
          <Typography data-testid="filter-uncommitted-indicator" color="warning.main">
            Changes not applied
          </Typography>
        ) : null}
        {showClearFilters ? (
          <Button
            type="button"
            onClick={clearDraftFilters}
            disabled={filtersDisabled}
            data-testid={`${testId}-clear-all`}
            sx={{ ...MOBILE_TYPE.button, color: "#D80000", minHeight: MOBILE_TOUCH_MIN_PX }}
          >
            Clear all
          </Button>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1 }}>
        {filterOrder.map((id) => {
          const summary = summaries[id] || {};
          const title = categoryLabels[id] || id;
          const valueHint = summary.isActive
            ? summary.valueLabel || summary.displayLabel || "Applied"
            : "Any";
          return (
            <Accordion
              key={id}
              disableGutters
              elevation={0}
              expanded={expanded === id}
              onChange={(_e, next) => setExpanded(next ? id : false)}
              sx={{
                before: { display: "none" },
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "transparent",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: MOBILE_TOUCH_MIN_PX,
                  px: 1.5,
                  "& .MuiAccordionSummary-content": {
                    my: 1,
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    pr: 1,
                  },
                }}
              >
                <Typography sx={{ ...MOBILE_TYPE.body, fontWeight: 700 }}>{title}</Typography>
                <Typography
                  sx={{
                    ...MOBILE_TYPE.helper,
                    color: summary.isActive ? "primary.main" : "text.secondary",
                    fontWeight: summary.isActive ? 600 : 400,
                    maxWidth: "55%",
                    textAlign: "right",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {valueHint}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                hidden={expanded !== id}
                sx={{ px: 1.5, pb: 2, pt: 0 }}
              >
                {panelContext && (keepPanelsMounted || expanded === id)
                  ? renderPanel(id, {
                      ...panelContext,
                      isPanelActive: open && expanded === id,
                      onApply: applyActivePanel,
                      registerPanelApply: (applyPanelDraft) =>
                        registerPanelApply(id, applyPanelDraft),
                    })
                  : null}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      <Box
        sx={{
          px: MOBILE_PAGE_PAD_X,
          pt: 1.5,
          pb: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          display: "flex",
          gap: 1,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={() => { discardFilters?.(); onClose(); }}
          sx={{
            ...MOBILE_TYPE.button,
            minHeight: MOBILE_TOUCH_MIN_PX,
            borderRadius: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={applyActivePanel}
          sx={{
            ...MOBILE_TYPE.button,
            minHeight: MOBILE_TOUCH_MIN_PX,
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          Apply
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}

MobileFilterSheet.propTypes = {
  keepPanelsMounted: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  controller: PropTypes.object,
  initialFilterId: PropTypes.string,
  showNaturalLanguageFilter: PropTypes.bool,
  chipOrder: PropTypes.arrayOf(PropTypes.string),
  categoryLabels: PropTypes.objectOf(PropTypes.string),
  renderPanel: PropTypes.func,
  testId: PropTypes.string,
};
